import { refreshAdminAccessToken } from "@/features/auth/api/adminAuthApi";
import { hasAdminSession } from "./adminSessionEmail";
import {
  getAdminAccessToken,
  getMsUntilAccessTokenExpiry,
  isAdminAccessTokenExpired,
  setAdminAccessToken,
} from "./adminAccessToken";

/** Refresh this long before access token expiry (default 60s). */
const DEFAULT_REFRESH_BUFFER_MS = 60_000;

function parseRefreshBufferMs(): number {
  const raw = process.env.REACT_APP_ACCESS_TOKEN_REFRESH_BUFFER_MS;
  if (!raw?.trim()) {
    return DEFAULT_REFRESH_BUFFER_MS;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0
    ? parsed
    : DEFAULT_REFRESH_BUFFER_MS;
}

export const ACCESS_TOKEN_REFRESH_BUFFER_MS = parseRefreshBufferMs();

export const ACCESS_TOKEN_REFRESH_CHECK_MS = 30_000;

let refreshInFlight: Promise<boolean> | null = null;
let sessionExpiredHandler: (() => void) | null = null;

export function registerAuthSessionExpiredHandler(handler: () => void): () => void {
  sessionExpiredHandler = handler;
  return () => {
    if (sessionExpiredHandler === handler) {
      sessionExpiredHandler = null;
    }
  };
}

export function notifyAuthSessionExpired(): void {
  sessionExpiredHandler?.();
}

export function shouldProactivelyRefreshAccessToken(): boolean {
  if (!hasAdminSession()) {
    return false;
  }

  const token = getAdminAccessToken();
  if (!token) {
    return true;
  }

  const msUntilExpiry = getMsUntilAccessTokenExpiry();
  if (msUntilExpiry === null) {
    return false;
  }

  return msUntilExpiry <= ACCESS_TOKEN_REFRESH_BUFFER_MS;
}

export async function refreshAdminSession(): Promise<boolean> {
  if (!hasAdminSession()) {
    return false;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const result = await refreshAdminAccessToken();
      setAdminAccessToken(result.access_token, result.expires_in);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function ensureFreshAccessTokenForRequest(): Promise<boolean> {
  if (!hasAdminSession()) {
    return false;
  }

  if (
    !getAdminAccessToken() ||
    isAdminAccessTokenExpired() ||
    shouldProactivelyRefreshAccessToken()
  ) {
    return refreshAdminSession();
  }

  return true;
}

export function isProtectedApiPath(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  const normalized = url.split("?")[0] ?? url;
  const authSegment = "/admin/auth/";
  if (!normalized.includes(authSegment)) {
    return true;
  }

  const publicAuthSuffixes = [
    "/login/start",
    "/login/verify",
    "/login/resend",
    "/refresh",
    "/logout",
  ] as const;

  return !publicAuthSuffixes.some((suffix) => normalized.endsWith(suffix));
}
